import gql from "graphql-tag";
import {
	FULL_CUSTOMER_FRAGMENT,
	NOTES_INFO,
	SALES_PAYMENT_INFO,
	PURCHASE_ORDER_INFO,
} from "./fragments";

const SEARCH_ORDERS = gql`
	query GetOrders(
		$searchTerm: String = null
		$offset: Int!
		$take: Int!
		$order: [SalesOrderSortInput!] = [{ modifiedAt: DESC }]
		$where: SalesOrderFilterInput = null
		$includeAllReps: Boolean = false
	) {
		salesOrders(
			searchTerm: $searchTerm
			order: $order
			skip: $offset
			take: $take
			where: $where
			includeAllReps: $includeAllReps
		) {
			totalCount
			items {
				salesOrderId
				customer {
					customerId
					customer {
						customerId
						organization
						customerNumber
					}
					contact {
						firstName
						lastName
						email
					}
				}
				orderNumber
				createdAt
				referenceNumber
				salesRepId
				status
				orderDate
				schoolPo
				balance
				total
				computedTotal
				computedBalance
				isSampleOrder
				isReturned
				returnedAt
				quoteId
				quote {
					quoteNumber
				}
			}
			pageInfo {
				hasNextPage
				hasPreviousPage
			}
		}
	}
`;

const SEARCH_QUOTES = gql`
	query GetQuotes(
		$searchTerm: String = null
		$offset: Int!
		$take: Int!
		$order: [QuoteSortInput!] = [{ modifiedAt: DESC }]
		$where: QuoteFilterInput = null
		$includeAllReps: Boolean = false
	) {
		quotes(
			searchTerm: $searchTerm
			order: $order
			skip: $offset
			take: $take
			where: $where
			includeAllReps: $includeAllReps
		) {
			totalCount
			items {
				quoteId
				quoteCustomer {
					customerId
					customer {
						customerId
						organization
						customerNumber
					}
					contact {
						firstName
						lastName
						email
					}
				}
				quoteNumber
				createdAt
				modifiedAt
				referenceNumber
				salesRepId
				status
				expectedOrderDate
				schoolPo
				balance
				total
				computedTotal
				computedBalance
				isSampleOrder
				order {
					salesOrderId
					orderNumber
				}
			}
			pageInfo {
				hasNextPage
				hasPreviousPage
			}
		}
	}
`;

const LIST_SALES_REPS = gql`
	query ListSalesReps($offset: Int! = 0, $take: Int! = 20, $where: SalesRepFilterInput = null) {
		salesReps(
			order: [{ inactiveDate: ASC }, { name: ASC }]
			take: $take
			skip: $offset
			where: $where
		) {
			items {
				salesRepId
				salesRepNumber
				name
				phoneNumber
				email
				userId
				inactiveDate
				sequence
			}
			pageInfo {
				hasNextPage
				hasPreviousPage
			}
		}
	}
`;

export const GET_SALES_ORDER = gql`
	${FULL_CUSTOMER_FRAGMENT}
	${NOTES_INFO}
	${SALES_PAYMENT_INFO}
	query GetSalesOrder($id: Int!) {
		salesOrderById(id: $id) {
			salesOrderId
			createdAt
			orderDate
			paymentTermsId
			processDate
			orderNumber
			salesRepId
			shippingMethodId
			status
			attribution
			schoolPo
			referenceNumber
			shippingAndHandling
			shippingAndHandlingDiscount
			shippingAndHandlingManualOverride
			tax
			taxRate
			taxRateManualOverride
			total
			balance
			quote {
				quoteNumber
				quoteId
				payments {
					...SalesPaymentInfo
				}
				notes {
					...NotesInfo
				}
			}
			customer {
				contactId
				billTo
				shipTo
				customer {
					...FullCustomer
				}
			}
			isCancelled
			isSampleOrder
		}
	}
`;

const GET_PAYMENT_TERMS = gql`
	query GetPaymentTerms {
		paymentTerms(take: 500) {
			items {
				paymentTermsId
				paymentTermsName
			}
		}
	}
`;

const GET_SHIPPING_METHODS = gql`
	query GetShippingMethods {
		shippingMethods(take: 500) {
			items {
				shippingMethodId
				shippingMethodName
				shippingCategoryId
			}
		}
	}
`;

const GET_SHIPPING_CATEGORIES = gql`
	query GetShippingCategories {
		shippingCategories(take: 500) {
			items {
				shippingCategoryId
				category
				Sequence
			}
		}
	}
`;

const GET_SHIPPING_METHOD_CALCULATIONS = gql`
	query GetShippingMethodCalculations {
		shippingMethodCalculations(take: 500) {
			items {
				shippingMethodCalculationId
				rangeStart
				rangeEnd
				ground
				threeDay
				twoDay
				overnight
				sequence
			}
		}
	}
`;

export const SEARCH_PURCHASE_ORDER_ITEMS = gql`
	query GetPurchaseOrderItems(
		$searchTerm: String = null
		$offset: Int! = 0
		$take: Int! = 20
		$order: [PurchaseOrderItemListingViewSortInput!] = [{ purchaseOrderDate: DESC }]
		$where: PurchaseOrderItemListingViewFilterInput = null
		$orderStatus: [String!] = null
	) {
		response: purchaseOrderItemListings(
			searchTerm: $searchTerm
			skip: $offset
			take: $take
			order: $order
			where: $where
			orderStatus: $orderStatus
		) {
			totalCount
			items {
				orderNumber: purchaseOrderNumber
				purchaseOrderId
				styleCode
				vendor
				ordered
				received
				orderType
				orderDate: purchaseOrderDate
				expectedDate
				isCancelled
				isLocked
				orderStatus
				dropShip
			}
			pageInfo {
				hasNextPage
				hasPreviousPage
			}
		}
	}
`;

export const SEARCH_PURCHASE_ORDERS = gql`
	query GetPurchaseOrders(
		$searchTerm: String = null
		$offset: Int! = 0
		$take: Int! = 20
		$orderDateSort: SortEnumType = DESC
		$where: PurchaseOrderListingFilterInput = null
	) {
		purchaseOrderListings(
			searchTerm: $searchTerm
			skip: $offset
			take: $take
			order: { purchaseOrderDate: $orderDateSort }
			where: $where
		) {
			items {
				orderNumber: purchaseOrderNumber
				salesOrderId
				purchaseOrderId
				vendor
				ordered
				received
				bo: backOrdered
				orderType
				orderDate: purchaseOrderDate
				orderStatus
			}
		}
	}
`;

export const PURCHASE_ORDER_BY_ID = gql`
	${PURCHASE_ORDER_INFO}
	query GetPurchaseOrderById($id: Int!) {
		purchaseOrderById(id: $id) {
			...PurchaseOrderInfo
			siblingItems {
				siblingPoId
				lineItemId
				lineNumber
			}
		}
	}
`;

export const PURCHASE_ORDER_BY_LINEITEMID = gql`
	${PURCHASE_ORDER_INFO}
	query GetPurchaseOrderByLineItemId($lineItemId: Long!) {
		purchaseOrderByLineItemId(lineItemId: $lineItemId) {
			...PurchaseOrderInfo
		}
	}
`;

export const CUSTOMER_ADDRESS_BY_ID = gql`
	query GetCustomerAddressById($id: Int!) {
		customerAddressById(id: $id) {
			addressId
			customer {
				customerId
				customerNumber
				organization
				addresses {
					addressId
					label
					addressLine1
					addressLine2
					addressTypeId
					city
					state
					postalCode
				}
				contacts {
					contactId
					contactTypeId
					primaryContact
					firstName
					middleName
					lastName
					workPhoneNumber
					cellPhoneNumber
					email
					isActive
				}
			}
		}
	}
`;
export const RECEIVED_ORDER_BY_ID = gql`
	query purchaseOrderById($id: Int!) {
		response: purchaseOrderById(id: $id) {
			id
			purchaseOrderNumber
			processType {
				name
			}
			vendorId
			vendor {
				companyName
			}
			salesOrderId
			lineItemId
			lineNumber
			parentItem {
				parentPoId
				parentLineItemId
				lineNumber
			}
			purchaseOrderDate
			createdAt
			isAttaching
			isCancelled
			dropShip
			notes {
				id
				noteText
				noteType
				createdBy
				modifiedBy
				modifiedAt
				createdAt
				purchaseOrderId
			}
			items {
				salesOrderLineItemId
				quantity
				ordered: quantity
				unitPrice
				totalSizes
				hasSizes
				styleHash
				itemOrdered {
					systemCategory
					inventoryItemId
					styleCode
					description
					processType
					processTypeId
				}
				inventoryItem {
					inventoryItemId
					sizeChart {
						sizeChartId
						name
						sizes {
							sizeId
							name
							sequence
						}
					}
				}
				sizes {
					size
					length
					sizeId
					lengthId
					quantity
					ordered: quantity
				}
				specialSizes {
					length
					quantity
					ordered: quantity
					size
				}
			}
			receivedItems {
				id
				itemId
				itemIndex
				lineItemId
				quantityReceived
				receivedDate
				styleHash
				sizesReceived {
					sizeId
					lengthId
					quantity
				}
				specialSizesReceived {
					size
					length
					quantity
				}
			}
		}
	}
`;

export const PURCHASE_ORDERS = gql`
	query purchaseOrders($where: PurchaseOrderFilterInput) {
		response: purchaseOrders(take: 500, where: $where) {
			items {
				id
				purchaseOrderNumber
				processType {
					name
				}
				isAttaching
				vendorId
				vendor {
					companyName
				}
				salesOrderId
				lineItemId
				lineNumber
				parentItem {
					parentPoId
					parentLineItemId
					lineNumber
				}
				purchaseOrderDate
				isCancelled
				dropShip
				createdAt
				notes {
					id
					noteText
					noteType
					createdBy
					modifiedBy
					modifiedAt
					createdAt
					purchaseOrderId
				}
				items {
					salesOrderLineItemId
					quantity
					ordered: quantity
					unitPrice
					totalSizes
					hasSizes
					styleHash
					itemOrdered {
						systemCategory
						inventoryItemId
						styleCode
						description
						processType
						processTypeId
					}
					inventoryItem {
						inventoryItemId
						sizeChart {
							sizeChartId
							name
							sizes {
								sizeId
								name
								sequence
							}
						}
					}
					sizes {
						size
						length
						sizeId
						lengthId
						quantity
						ordered: quantity
					}
					specialSizes {
						size
						length
						quantity
						ordered: quantity
					}
				}
				receivedItems {
					id
					itemId
					itemIndex
					lineItemId
					quantityReceived
					receivedDate
					styleHash
					sizesReceived {
						sizeId
						lengthId
						quantity
					}
					specialSizesReceived {
						size
						length
						quantity
					}
				}
			}
		}
	}
`;

// Note: GET_ORDER_BY_PICK_TICKET query removed - using REST API endpoint instead
// The GraphQL schema doesn't properly expose lineItems for filtering or querying

const GET_PICK_TICKET_PRINT_DATA = gql`
	query GetPickTicketPrintData($orderId: Int!, $pickTicketIds: [Long!]!) {
		pickTicketPrintData(orderId: $orderId, pickTicketIds: $pickTicketIds) {
			order {
				salesOrderId
				orderNumber
				orderDate
				createdAt
				paymentTermsId
				processDate
				salesRepId
				shippingMethodId
				status
				attribution
				schoolPo
				referenceNumber
				shippingAndHandling
				shippingAndHandlingDiscount
				shippingAndHandlingManualOverride
				tax
				taxRate
				total
				balance
				isCancelled
				customer {
					contactId
					billTo
					shipTo
					customer {
						organization
						customerId
						customerNumber
						salesTaxId
						addresses {
							addressId
							label
							addressLine1
							addressLine2
							addressTypeId
							city
							state
							postalCode
							isPrimaryAddress
							isActive
							contactId
							organization
							department
							contact {
								contactId
								firstName
								lastName
								email
								workPhoneNumber
								cellPhoneNumber
							}
						}
						contacts {
							contactId
							primaryContact
							firstName
							lastName
							workPhoneNumber
							cellPhoneNumber
							email
							isActive
						}
					}
				}
				quote {
					quoteId
					quoteNumber
					notes {
						noteId
						noteText
						noteType
						createdAt
						createdByName
					}
					payments {
						paymentId
						amount
						paymentDate
						paymentMethod
						referenceNumber
						schoolOrOrganization
					}
				}
			}
			pickTickets {
				pickTicketId
				type
				normalPickTicketId
				shippingDate
				trackingNumber
				lineItems {
					lineItemId
					lineNumber
					itemId
					quantity
					qtyCommitted
					qtyShipped
					price
					note
					braidPosition
					dropShip
					includePrice
					type
					status
					bin
					inventoryItem {
						inventoryItemId
						styleCode
						vendorPartNumber
						description
						stockItems
						systemCategoryId
						systemCategory
						includeWithParentPO
						processTypeId
						bin {
							binId
							name
							location
						}
						inventoryInfo {
							binNumber
							onHand
							committed
						}
						colorOptions {
							colorOptionId
							name
							description
							hexCode
							position
							type
							colorPalettes {
								colorPaletteId
								name
								colors {
									colorId
									name
									label
									hexCode
									isCustomColor
									isRetired
									isVisible
								}
							}
						}
						sizeChart {
							sizeChartId
							name
							sizes {
								sizeId
								name
								sortOrder
								inventoryInfo {
									binNumber
									onHand
									committed
								}
							}
						}
						subItems {
							subItemId
							styleCode
							description
							colorOptions {
								colorOptionId
								name
								description
								hexCode
								position
								type
							}
						}
					}
					lettering {
						letterPositionId
						letterPosition
						text
						description
					}
					matchPreviousPoNumber
					sizeSelections {
						sizeId
						sizeName
						quantity
						length
					}
					specialSizeSelections {
						size
						length
						quantity
					}
					colorSelections {
						colorId
						colorOptionId
						colorPaletteId
						colorName
						colorOption
						colorDescription
						text
						hexCode
					}
					freeTextSelections {
						colorOptionId
						text
					}
					children {
						lineItemId
						lineNumber
						itemId
						quantity
						qtyCommitted
						qtyShipped
						price
						note
						braidPosition
						dropShip
						includePrice
						type
						status
						bin
						inventoryItem {
							inventoryItemId
							styleCode
							vendorPartNumber
							description
							stockItems
							systemCategoryId
							systemCategory
							includeWithParentPO
							processTypeId
							bin {
								binId
								name
								location
							}
							inventoryInfo {
								binNumber
								onHand
								committed
							}
							colorOptions {
								colorOptionId
								name
								description
								hexCode
								position
								type
								colorPalettes {
									colorPaletteId
									name
									colors {
										colorId
										name
										label
										hexCode
										isCustomColor
										isRetired
										isVisible
									}
								}
							}
							sizeChart {
								sizeChartId
								name
								sizes {
									sizeId
									name
									sortOrder
								}
							}
						}
						lettering {
							letterPositionId
							letterPosition
							text
							description
						}
						sizeSelections {
							sizeId
							sizeName
							quantity
							length
						}
						specialSizeSelections {
							size
							length
							quantity
						}
						colorSelections {
							colorId
							colorOptionId
							colorPaletteId
							colorName
							colorOption
							colorDescription
							text
							hexCode
						}
						freeTextSelections {
							colorOptionId
							text
						}
					purchaseOrders {
						purchaseOrderId
						purchaseOrderNumber
						purchaseOrderDate
						expectedDate
						orderStatus
						isLocked
						isCancelled
						dropShip
						isAttaching
						processTypeId
						contactId
						customerAddressId
						vendor {
							vendorId
							companyName
						}
						vendorAddress {
							addressLine1
							addressLine2
							addressLine3
							city
							state
							postalCode
						}
						receivedItems {
							receivedDate
							quantityReceived
						}
					}
					}
					purchaseOrders {
						purchaseOrderId
						purchaseOrderNumber
						purchaseOrderDate
						expectedDate
						orderStatus
						isLocked
						isCancelled
						dropShip
						isAttaching
						processTypeId
						contactId
						customerAddressId
						vendor {
							vendorId
							companyName
						}
						vendorAddress {
							addressLine1
							addressLine2
							addressLine3
							city
							state
							postalCode
						}
						parentItem {
							purchaseOrderId
							lineItemId
							lineNumber
							itemOrdered {
								inventoryItemId
								styleCode
								vendorPartNumber
								description
								systemCategoryId
								systemCategory
							}
						}
						childItems {
							purchaseOrderId
							lineItemId
							lineNumber
							purchaseOrderNumber
							orderStatus
							quantity
							isParentItem
							isAttaching
							braidPosition
							note
							matchPreviousPoNumber
							lettering {
								letterPositionId
								text
							}
							colorSelections {
								colorId
								colorOptionId
								colorPaletteId
								colorName
								colorOption
								colorDescription
								text
								hexCode
							}
							freeTextSelections {
								colorOptionId
								text
							}
							sizeSelections {
								sizeId
								sizeName
								quantity
								length
							}
							specialSizeSelections {
								size
								length
								quantity
							}
							inventoryItem {
								inventoryItemId
								styleCode
								vendorPartNumber
								description
								systemCategoryId
								systemCategory
								processTypeId
							}
							vendor {
								vendorId
								companyName
							}
							vendorAddress {
								addressLine1
								addressLine2
								addressLine3
								city
								state
								postalCode
							}
						}
						attachItems {
							attachPoId
							purchaseOrderNumber
							orderStatus
							vendor {
								vendorId
								companyName
							}
							vendorAddress {
								addressLine1
								addressLine2
								addressLine3
								city
								state
								postalCode
							}
						}
						receivedItems {
							receivedDate
							quantityReceived
						}
					}
				}
			}
			supportingData {
				processTypes {
					processTypeId
					name
					description
					requireAttaching
					includeParent
				}
				letterPositions {
					letterPositionId
					code
					description
				}
				shippingMethods {
					shippingMethodId
					name
				}
				paymentTerms {
					paymentTermsId
					name
				}
				paymentMethods {
					paymentMethodId
					name
				}
				salesReps {
					salesRepId
					name
					email
					phoneNumber
				}
			}
		}
	}
`;

const GET_ACTIVITY_LOGS = gql`
	query GetActivityLogs(
		$where: ActivityLogFilterInput
		$order: [ActivityLogSortInput!]
		$skip: Int
		$take: Int
	) {
		activityLogs(where: $where, order: $order, skip: $skip, take: $take) {
			totalCount
			items {
				activityLogId
				requestName
				outcome
				userName
				timestamp
				durationMs
				failureMessage
				quoteId
				orderId
				lineItemId
				pickTicketId
				purchaseOrderId
				entityNumber
				requestSummary
			}
		}
	}
`;

// Named exports for direct import
export { GET_PICK_TICKET_PRINT_DATA, GET_ACTIVITY_LOGS };

export default {
	SEARCH_ORDERS,
	SEARCH_QUOTES,
	LIST_SALES_REPS,
	GET_SALES_ORDER,
	GET_PAYMENT_TERMS,
	GET_SHIPPING_METHODS,
	GET_SHIPPING_CATEGORIES,
	GET_SHIPPING_METHOD_CALCULATIONS,
	SEARCH_PURCHASE_ORDER_ITEMS,
	SEARCH_PURCHASE_ORDERS,
	PURCHASE_ORDER_BY_ID,
	CUSTOMER_ADDRESS_BY_ID,
	GET_PICK_TICKET_PRINT_DATA,
	GET_ACTIVITY_LOGS,
};
